<template>
  <article class="resume-card">
    <div v-if="resume.lastVisited" class="resume-card__last-visited">
      <BasicDate
        :date="resume.lastVisited.date"
        :title="resume.lastVisited.title"
      />
    </div>

    <aside class="resume-card__aside">
      <BasicAvatar
        :href="resume.href"
        :image="resume.avatar"
        size="medium"
        roundness="full"
      />
    </aside>

    <div class="resume-card__main">
      <div class="resume-card__body">
        <h2 class="resume-card__title">
          <a
            :href="resume.href"
            class="resume-card__title-link"
            target="_blank"
            v-html="resume.title"
          />
          <InlineIconsList v-if="resume.isExpert">
            <ExpertIcon />
          </InlineIconsList>
        </h2>

        <div
          v-if="specialization && specialization.length"
          class="resume-card__specialization"
        >
          <InlineList :items="specialization" v-slot="{ item }">
            <span v-html="item" />
          </InlineList>
        </div>

        <div class="resume-card__offer">
          <InlineList
            separator-color="inherit"
            :items="offer"
            v-slot="{ item }"
          >
            <span v-text="item" />
          </InlineList>
        </div>

        <slot name="body">
          <ContentSection
            v-if="skills && skills.length"
            appearance="skills"
            title="Профессиональные навыки"
          >
            <SkillsListShow
              :skills="skills"
              :user-login="resume.id"
              has-html
              :is-owner="isOwner"
              :is-guest="isGuest"
            />
          </ContentSection>
          <div v-if="resume.hasTests" class="resume-card__tests">
            <SvgIcon icon="check" size="24" />
            <BasicButton
              :href="`${resume.href}/#specs_tests`"
              appearance="pseudo-link-thin"
            >
              Пройдены тесты по специализации
            </BasicButton>
          </div>
          <ContentSection
            v-if="resume.age"
            title="Возраст"
            appearance="resume-card"
          >
            <span>{{ resume.age.title }}</span>
          </ContentSection>

          <ContentSection
            v-if="experience && experience.length"
            title="Опыт работы"
            appearance="resume-card"
          >
            <div @click="openCompanies()" class="resume-card__experience">
              <InlineList :items="experience" v-slot="{ item }">
                <span v-text="item" />
              </InlineList>
              <ArrowIcon
                v-if="resume.companiesHistory.length"
                color="muted"
                class="resume-card__experience-arrow"
                :direction="isOpenCompanies ? 'top' : 'bottom'"
                size="24"
              />
            </div>
            <div v-if="isOpenCompanies" class="resume-card__companies">
              <InlineList
                v-for="(company, index) in resume.companiesHistory"
                :key="index"
                :items="[company.companyName, company.experience]"
                v-slot="{ item }"
              >
                <span v-html="item" />
              </InlineList>
            </div>
          </ContentSection>

          <ContentSection
            v-if="
              resume.experienceDescriptions &&
              resume.experienceDescriptions.length
            "
          >
            <InlineList
              :items="resume.experienceDescriptions"
              separator="ellipsis"
              v-slot="{ item }"
            >
              <span v-html="item" />
            </InlineList>
          </ContentSection>

          <ContentSection
            v-if="
              resume.experienceSpecializations &&
              resume.experienceSpecializations.length
            "
          >
            <InlineList
              :items="resume.experienceSpecializations"
              v-slot="{ item }"
            >
              <span v-html="item" />
            </InlineList>
          </ContentSection>

          <ContentSection
            v-if="location && location.length"
            title="Город"
            appearance="resume-card"
          >
            <InlineList :items="location" v-slot="{ item }">
              <LocationItem
                v-if="item.location"
                :location="item.location"
                has-html
              />
              <span v-else v-html="item.title" />
            </InlineList>
          </ContentSection>

          <ContentSection
            v-if="communities && communities.length"
            title="Сообщества"
            appearance="contacts"
          >
            <CommunityInlineListNew :communities="communities" />
          </ContentSection>

          <ContentSection
            v-if="contacts && contacts.items.length"
            title="Контакты"
            appearance="contacts"
          >
            <ContactsInlineList :contacts="contacts" />
          </ContentSection>

          <ContentSection
            v-if="resume.aboutArray && resume.aboutArray.length"
            title="О себе"
            appearance="resume-card"
          >
            <InlineList :items="resume.aboutArray" v-slot="{ item }">
              <span v-html="item" />
            </InlineList>
          </ContentSection>

          <TransitionExpand v-if="hasHiddenSections">
            <div v-if="!collapsed">
              <ContentSection
                v-if="resume.foreignLanguages && resume.foreignLanguages.length"
                title="Знание языков"
                appearance="resume-card"
              >
                <InlineList :items="resume.foreignLanguages" v-slot="{ item }">
                  <span v-text="item.title" />
                </InlineList>
              </ContentSection>

              <ContentSection
                v-if="resume.citizenships && resume.citizenships.length"
                title="Гражданство"
                appearance="resume-card"
              >
                <InlineList :items="resume.citizenships" v-slot="{ item }">
                  <span v-text="item.title" />
                </InlineList>
              </ContentSection>

              <ContentSection
                v-if="education && education.length"
                title="Высшее образование"
                appearance="resume-card"
              >
                <InlineList :items="education" v-slot="{ item }">
                  <BasicLink
                    v-if="item.href"
                    :href="item.href"
                    :title="item.title"
                    appearance="dark"
                  />
                  <span v-else v-text="item.title" />
                </InlineList>
                <BasicText
                  v-if="moreEducation"
                  color="muted"
                  size="medium"
                  class="resume-card__more-education"
                >
                  {{ moreEducation }}
                </BasicText>
              </ContentSection>

              <ContentSection
                v-if="additionalEducation && additionalEducation.length"
                title="Дополнительное образование"
                appearance="resume-card"
              >
                <InlineList :items="additionalEducation" v-slot="{ item }">
                  <BasicLink
                    v-if="item.href"
                    :href="item.href"
                    :title="item.title"
                    appearance="dark"
                  />
                  <span v-else v-text="item.title" />
                </InlineList>
              </ContentSection>
            </div>
          </TransitionExpand>

          <TransitionExpand>
            <div v-html="resume.esDebugInfo"></div>
          </TransitionExpand>
        </slot>
      </div>
      <slot v-if="withFooter" name="footer">
        <div
          class="resume-card__footer"
          :class="
            bem('resume-card__footer', {
              collapsed: hasHiddenSections && collapsed,
            })
          "
        >
          <div v-if="hasHiddenSections" class="resume-card__collapse-button">
            <BasicButton
              appearance="resume-toggle"
              size="m"
              @click="onCollapseButtonClick"
            >
              {{ buttonTitle }}
            </BasicButton>
          </div>

          <div class="resume-card__actions">
            <JobInviteButton
              v-if="currentResume.jobInviteButton"
              :job-invite-button="currentResume.jobInviteButton"
              invited-from="resumes_card"
              @success="emitSuccessInvite"
              :company-id="visitorCompanyId"
              :full-width="mediaQueries.mobile"
            />
            <div class="resume-card__actions-block">
              <BasicButton
                v-if="resume.conversationHref"
                :href="resume.conversationHref"
                :full-width="mediaQueries.mobile"
                appearance="secondary"
                size="m"
                :title="`Диалог с пользователем ${resume.title}`"
              >
                Написать
              </BasicButton>

              <div
                v-if="!mediaQueries.mobile && showCoworkersList"
                class="resume-card__conversations"
              >
                <InlineCoworkersList :coworkers="resume.coworkers" />
              </div>
              <template v-if="showContactsButton">
                <BasicButton
                  v-if="!contacts.contactsAvailable && contacts.conditions"
                  :disabled="contactsIsLoading"
                  @click="openContacts"
                  appearance="tertiary"
                  class="resume-card__contacts"
                  size="m"
                  >Открыть контакты
                </BasicButton>
                <div
                  v-if="contacts.contactsAvailable"
                  class="resume-card__contacts-opened"
                >
                  <SvgIcon icon="check" size="24" />
                  Контакты открыты
                </div>
              </template>
            </div>
            <div
              v-if="mediaQueries.mobile && showCoworkersList"
              class="resume-card__conversations"
            >
              <InlineCoworkersList :coworkers="resume.coworkers" />
            </div>
          </div>
        </div>
      </slot>
    </div>
    <LimitModal
      v-if="isOpenLimitModal"
      title="Контакты недоступны"
      :modal-info="contacts.conditions"
      @close="closeLimitModal"
    />
  </article>
</template>

<script>
import { showErrorNotification } from 'src/services/api.js';
import { PersonalResource } from 'src/services/personal.js';
import { bem } from 'src/utils/bem.js';

import BasicAvatar from 'src/components/BasicAvatar/BasicAvatar.vue';
import BasicButton from 'src/components/BasicButton/BasicButton.vue';
import BasicDate from 'src/components/BasicDate/BasicDate.vue';
import BasicLink from 'src/components/BasicLink/BasicLink.vue';
import CommunityInlineListNew from 'src/components/CommunityInlineListNew/CommunityInlineListNew.vue';
import ContactsInlineList from 'src/components/ContactsInlineList/ContactsInlineList.vue';
import ExpertIcon from 'src/components/experts/ExpertIcon/ExpertIcon.vue';
import ArrowIcon from 'src/components/icons/ArrowIcon/ArrowIcon.vue';
import SvgIcon from 'src/components/icons/SvgIcon/SvgIcon.vue';
import InlineCoworkersList from 'src/components/InlineCoworkersList/InlineCoworkersList.vue';
import InlineIconsList from 'src/components/InlineIconsList/InlineIconsList.vue';
import InlineList from 'src/components/InlineList/InlineList.vue';
import JobInviteButton from 'src/components/JobInviteButton/JobInviteButton.vue';
import LimitModal from 'src/components/LimitModal/LimitModal.vue';
import LocationItem from 'src/components/LocationItem/LocationItem.vue';
import ContentSection from 'src/components/sections/ContentSection/ContentSection.vue';
import SkillsListShow from 'src/components/SkillsListShow/SkillsListShow.vue';
import BasicText from 'src/components/text/BasicText/BasicText.vue';
import TransitionExpand from 'src/components/transitions/TransitionExpand/TransitionExpand.vue';

export default {
  name: 'ResumeCard',
  components: {
    SkillsListShow,
    ArrowIcon,
    BasicText,
    InlineIconsList,
    ExpertIcon,
    LocationItem,
    TransitionExpand,
    InlineCoworkersList,
    BasicButton,
    CommunityInlineListNew,
    ContentSection,
    SvgIcon,
    BasicLink,
    InlineList,
    BasicAvatar,
    BasicDate,
    ContactsInlineList,
    LimitModal,
    JobInviteButton,
  },
  inject: ['visitor', 'mediaQueries'],
  props: {
    resume: {
      type: Object,
      required: true,
    },
    withFooter: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['invite'],
  provide() {
    return {
      contactsConditions: this.contacts.conditions,
    };
  },
  data() {
    return {
      collapsed: true,
      isOpenCompanies: this.resume.openCompaniesHistory,
      isOpenLimitModal: false,
      currentResume: this.resume,
      contactsIsLoading: false,
    };
  },
  methods: {
    bem,
    onCollapseButtonClick(event) {
      this.collapsed = !this.collapsed;
      event.target.blur();
    },
    openCompanies() {
      if (!this.resume.companiesHistory.length) return;
      this.isOpenCompanies = !this.isOpenCompanies;
    },
    async openContacts() {
      if (this.contacts.conditions?.action?.type === 'button') {
        this.contactsIsLoading = true;
        try {
          const response = await PersonalResource.getUserContacts(
            this.resume.id,
          );
          this.currentResume.contacts = response.data.contacts;
          if (this.currentResume.jobInviteButton?.showHint) {
            this.currentResume.jobInviteButton.showHint = false;
          }
        } catch (error) {
          showErrorNotification(error);
        } finally {
          this.contactsIsLoading = false;
        }
      } else {
        this.isOpenLimitModal = true;
      }
    },
    closeLimitModal() {
      this.isOpenLimitModal = false;
    },
    emitSuccessInvite(response) {
      this.$emit('invite');
      if (response && response.contacts) {
        this.currentResume.contacts = response.contacts;
      }
    },
  },
  computed: {
    visitorCompanyId() {
      return this.visitor?.company?.id;
    },
    isOwner() {
      return this.visitor?.login === this.resume.id;
    },
    isGuest() {
      return this.visitor.isGuest;
    },
    showActions() {
      return this.resume.conversationHref || this.resume.jobInviteButton;
    },
    communities() {
      return this.resume.communities;
    },
    additionalEducation() {
      return this.resume.additionalEducation;
    },
    contacts() {
      if (!this.currentResume.contacts)
        return {
          items: [],
          contactsAvailable: false,
          conditions: null,
        };
      const displayedContacts = [
        'phone_mobile',
        'telegram',
        'email',
        'vkontakte',
        'facebook',
        'linkedin',
        'skype',
        'link',
      ];
      const contactsItems = [];
      displayedContacts.forEach((item) => {
        const contact = this.currentResume.contacts.items.find(
          (contactItem) => contactItem.kind === item,
        );
        if (
          contact &&
          !contactsItems.find((contactItem) => contactItem.kind === item)
        ) {
          contactsItems.push(contact);
        }
      });

      return {
        ...this.currentResume.contacts,
        items: contactsItems,
      };
    },
    showContactsButton() {
      if (this.isOwner) return false;
      return this.contacts && this.contacts.items.length;
    },
    specialization() {
      const { resume } = this;
      const result = [];

      if (resume.specializations.length !== 0) {
        resume.specializations.forEach((specialization) =>
          result.push(specialization.title),
        );
      }

      if (resume.qualification) {
        result.push(resume.qualification.title);
      }

      return result;
    },
    offer() {
      const { resume } = this;
      const result = [];

      if (resume.salary) {
        result.push(resume.salary.title);
      }

      if (resume.availability) {
        result.push(resume.availability.title);
      }

      return result;
    },
    location() {
      const { resume } = this;
      const result = [];

      if (resume.location) {
        result.push({ location: resume.location });
      }

      const prefix = resume.gender > 0 ? 'Готова' : 'Готов';

      if (resume.remoteWork) {
        result.push({ title: `${prefix} к удалённой работе` });
      }

      if (resume.relocation) {
        result.push({ title: `${prefix} к переезду` });
      }

      return result;
    },
    skills() {
      return this.resume.skills;
    },
    experience() {
      const { resume } = this;
      const result = [];

      if (resume.companiesCount) result.push(resume.companiesCount);
      if (resume.experience) result.push(resume.experience.title);

      return result;
    },
    education() {
      const { resume } = this;
      const result = [];

      if (resume.education) {
        const edu = resume.education;
        if (edu.university) {
          result.push({
            title: edu.university.title,
            href: edu.university.href,
          });
        }
        if (edu.faculty) {
          result.push({ title: edu.faculty });
        }
        if (edu.duration) {
          result.push({ title: edu.duration.title });
        }
      }

      return result;
    },
    moreEducation() {
      if (!this.resume.moreUniversityCount) return null;

      return `Ещё ${this.resume.moreUniversityCount} в профиле`;
    },
    buttonTitle() {
      return this.collapsed ? 'Развернуть' : 'Свернуть';
    },
    hasHiddenSections() {
      return [
        this.education,
        this.additionalEducation,
        this.resume.foreignLanguages,
        this.resume.citizenships,
      ].some((data) => data && data.length);
    },
    showCoworkersList() {
      return this.resume.coworkers && this.resume.coworkers.length;
    },
  },
};
</script>
